import { useCallback, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronDown, Phone } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { getRouter } from "@/services/router/router";
import stellar from "@/services/stellar";

const RESPONSE_FORM_SCHEMA = z.object({
	message: z.string().trim().min(1, "Introduza uma resposta."),
});

const QUOTATION_FORM_SCHEMA = z.object({
	value: z
		.number()
		.nonnegative("Introduza um valor válido.")
		.finite("Introduza um valor válido."),
});

type ResponseFormValues = z.output<typeof RESPONSE_FORM_SCHEMA>;

type QuotationFormValues = z.output<typeof QUOTATION_FORM_SCHEMA>;

type ClientContactClient = {
	id?: string;
	name?: string | null;
	phone?: string | null;
	email?: string | null;
	taxNumber?: string | null;
	isStore?: boolean | null;
};

type ClientContactRepairLog = {
	type: string;
	message: string;
};

type ClientContactRepairTechnician = {
	external?: boolean | null;
};

type ClientContactRepairEquipment = {
	brand?: string | null;
	model?: string | null;
};

type ClientContactRepair = {
	id: string;
	repairNumber?: string | number | null;
	storeRepairIdentifier?: string | null;
	clientEquipment?: ClientContactRepairEquipment | null;
	value?: number | null;
	quotation?: number | null;
	state?: string | null;
	technician?: ClientContactRepairTechnician | null;
	logs?: ClientContactRepairLog[] | null;
};

type ClientContactEntry = {
	id: string;
	client?: ClientContactClient | null;
	repair?: ClientContactRepair | null;
	message?: string | null;
	isQuotation?: boolean | null;
	isContactRequest?: boolean | null;
	missedCall?: boolean | null;
	waitingAnswer?: boolean | null;
};

type DialogState =
	| { type: "none" }
	| { type: "client"; client: ClientContactClient }
	| { type: "approve"; contact: ClientContactEntry }
	| { type: "reject"; contact: ClientContactEntry }
	| { type: "response"; contact: ClientContactEntry }
	| { type: "setValue"; contact: ClientContactEntry };

type ClientContactsTableProps = {
	data: ClientContactEntry[];
	onUpdate: () => void;
};

const formatCurrency = (value: number) =>
	new Intl.NumberFormat("pt-PT", {
		style: "currency",
		currency: "EUR",
	}).format(value);

const stripHtml = (value: string) => value.replace(/<[^>]*>/g, "");

const parseNumberInput = (value: string) => {
	const parsed = Number(value);
	return Number.isNaN(parsed) ? 0 : parsed;
};

const buildInitials = (name?: string | null) => {
	if (!name) {
		return "?";
	}

	const parts = name
		.trim()
		.split(/\s+/)
		.filter((part) => part.length > 0);
	if (parts.length === 0) {
		return "?";
	}

	return parts
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase())
		.filter((value): value is string => Boolean(value))
		.join("");
};

const getQuotationValue = (contact: ClientContactEntry) => {
	const value = contact.repair?.value ?? contact.repair?.quotation ?? 0;
	return Number(value) || 0;
};

const getEquipmentLabel = (repair?: ClientContactRepair | null) => {
	if (!repair?.clientEquipment) {
		return "-";
	}

	const brand = repair.clientEquipment.brand?.trim() ?? "";
	const model = repair.clientEquipment.model?.trim() ?? "";
	const label = `${brand} ${model}`.trim();

	return label.length > 0 ? label : "-";
};

const getQuotationMessage = (entry: ClientContactEntry) => {
	const repair = entry.repair;

	if (!repair) {
		return "-";
	}

	const value = repair.value ?? repair.quotation ?? 0;

	if (!repair.logs) {
		return "Logs invalidos!";
	}

	const quotationLogEntry = repair.logs.find(
		(logEntry) => logEntry.type === "repair.quotation",
	);

	if (!quotationLogEntry) {
		return "-";
	}

	if (repair.technician?.external) {
		return `[${formatCurrency(value)}] ${quotationLogEntry.message}`;
	}

	return quotationLogEntry.message;
};

const isToSetRepairValue = (entry: ClientContactEntry) =>
	Boolean(
		entry.repair &&
			entry.repair.state === "waiting_quotation_approve" &&
			!entry.repair.value &&
			entry.repair.technician?.external,
	);

export const ClientContactsTable: React.FC<ClientContactsTableProps> = ({
	data,
	onUpdate,
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [dialogState, setDialogState] = useState<DialogState>({ type: "none" });

	const responseForm = useForm<ResponseFormValues>({
		resolver: zodResolver(RESPONSE_FORM_SCHEMA),
		defaultValues: { message: "" },
	});

	const approveForm = useForm<QuotationFormValues>({
		resolver: zodResolver(QUOTATION_FORM_SCHEMA),
		defaultValues: { value: 0 },
	});

	const setValueForm = useForm<QuotationFormValues>({
		resolver: zodResolver(QUOTATION_FORM_SCHEMA),
		defaultValues: { value: 0 },
	});

	const closeDialog = useCallback(() => {
		setDialogState({ type: "none" });
		responseForm.reset({ message: "" });
		approveForm.reset({ value: 0 });
		setValueForm.reset({ value: 0 });
	}, [approveForm, responseForm, setValueForm]);

	const openClientDialog = useCallback((client: ClientContactClient) => {
		setDialogState({ type: "client", client });
	}, []);

	const openApproveDialog = useCallback(
		(contact: ClientContactEntry) => {
			approveForm.reset({ value: getQuotationValue(contact) });
			setDialogState({ type: "approve", contact });
		},
		[approveForm],
	);

	const openResponseDialog = useCallback(
		(contact: ClientContactEntry) => {
			responseForm.reset({ message: "" });
			setDialogState({ type: "response", contact });
		},
		[responseForm],
	);

	const openSetValueDialog = useCallback(
		(contact: ClientContactEntry) => {
			setValueForm.reset({ value: getQuotationValue(contact) });
			setDialogState({ type: "setValue", contact });
		},
		[setValueForm],
	);

	const openRejectDialog = useCallback((contact: ClientContactEntry) => {
		setDialogState({ type: "reject", contact });
	}, []);

	const handleShowRepair = useCallback(async (contact: ClientContactEntry) => {
		const repairId = contact.repair?.id;
		if (!repairId) {
			return;
		}

		const router = await getRouter();
		router.push(`/repair/${repairId}`);
	}, []);

	const handleMarkProcessed = useCallback(
		async (contact: ClientContactEntry, message?: string) => {
			setIsLoading(true);
			try {
				await stellar.action("markClientNotificationsAsProcessed", {
					id: contact.id,
					message,
				});
				onUpdate();
				toast.success("Mensagem processada!");
			} catch {
				toast.error("Não foi possível processar a mensagem.");
			} finally {
				setIsLoading(false);
			}
		},
		[onUpdate],
	);

	const handleMarkAsWaiting = useCallback(
		async (contact: ClientContactEntry) => {
			setIsLoading(true);
			try {
				await stellar.action("markContactAsWaitingResponse", contact);
				onUpdate();
			} catch {
				toast.error("Não foi possível atualizar o contacto.");
			} finally {
				setIsLoading(false);
			}
		},
		[onUpdate],
	);

	const handleMarkAsNotAnswered = useCallback(
		async (contact: ClientContactEntry) => {
			setIsLoading(true);
			try {
				await stellar.action("markContactAsNotAnswered", { id: contact.id });
				onUpdate();
			} catch {
				toast.error("Não foi possível atualizar o contacto.");
			} finally {
				setIsLoading(false);
			}
		},
		[onUpdate],
	);

	const handleApproveQuotation = useCallback(
		async (values: QuotationFormValues) => {
			if (dialogState.type !== "approve") {
				return;
			}

			const repairId = dialogState.contact.repair?.id;
			if (!repairId) {
				toast.error("Reparação indisponível para aprovar o orçamento.");
				return;
			}

			setIsLoading(true);
			try {
				await stellar.action("approveQuotation", {
					repair: repairId,
					value: values.value,
				});
				onUpdate();
				toast.success("Orçamento aprovado!");
				closeDialog();
			} catch {
				toast.error("Não foi possível aprovar o orçamento.");
			} finally {
				setIsLoading(false);
			}
		},
		[closeDialog, dialogState, onUpdate],
	);

	const handleRejectQuotation = useCallback(async () => {
		if (dialogState.type !== "reject") {
			return;
		}

		const repairId = dialogState.contact.repair?.id;
		if (!repairId) {
			toast.error("Reparação indisponível para recusar o orçamento.");
			return;
		}

		setIsLoading(true);
		try {
			await stellar.action("rejectQuotation", { repair: repairId });
			onUpdate();
			toast.success("Orçamento recusado!");
			closeDialog();
		} catch {
			toast.error("Não foi possível recusar o orçamento.");
		} finally {
			setIsLoading(false);
		}
	}, [closeDialog, dialogState, onUpdate]);

	const handleRepairValue = useCallback(
		async (values: QuotationFormValues) => {
			if (dialogState.type !== "setValue") {
				return;
			}

			const repairId = dialogState.contact.repair?.id;
			if (!repairId) {
				toast.error("Reparação indisponível para alterar o valor.");
				return;
			}

			setIsLoading(true);
			try {
				await stellar.action("editRepair", {
					id: repairId,
					value: values.value,
				});
				onUpdate();
				toast.success("Valor da reparação alterado.");
				closeDialog();
			} catch {
				toast.error("Não foi possível alterar o valor.");
			} finally {
				setIsLoading(false);
			}
		},
		[closeDialog, dialogState, onUpdate],
	);

	const handleResponseSubmit = useCallback(
		async (values: ResponseFormValues) => {
			if (dialogState.type !== "response") {
				return;
			}

			await handleMarkProcessed(dialogState.contact, values.message);
			closeDialog();
		},
		[closeDialog, dialogState, handleMarkProcessed],
	);

	const handleMarkProcessedClick = useCallback(
		(contact: ClientContactEntry) => {
			if (contact.isContactRequest === true && contact.repair) {
				openResponseDialog(contact);
				return;
			}

			void handleMarkProcessed(contact);
		},
		[handleMarkProcessed, openResponseDialog],
	);

	const rows = useMemo(
		() =>
			data.map((entry) => {
				const clientName = entry.client?.name?.trim() || "-";
				const clientPhone = entry.client?.phone?.trim() || "-";
				const repairNumber = entry.repair?.repairNumber ?? "-";
				const storeRepairId =
					entry.repair && entry.client?.isStore
						? (entry.repair.storeRepairIdentifier ?? "-")
						: "-";
				const message = entry.isQuotation
					? stripHtml(getQuotationMessage(entry))
					: entry.message || "-";

				return {
					key: entry.id,
					entry,
					clientName,
					clientPhone,
					repairNumber,
					storeRepairId,
					message,
				};
			}),
		[data],
	);

	return (
		<Card className="relative border-border/60 bg-card/70">
			<CardContent className="p-0">
				<Table>
					<TableHeader>
						<TableRow className="bg-muted/50">
							<TableHead className="w-[85px]">#</TableHead>
							<TableHead className="w-[80px]">Loja</TableHead>
							<TableHead>Cliente</TableHead>
							<TableHead className="w-[140px]">Contacto</TableHead>
							<TableHead className="w-[200px]">Equipamento</TableHead>
							<TableHead>Mensagem</TableHead>
							<TableHead className="w-[200px] text-right">Ações</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{rows.length === 0 ? (
							<TableRow>
								<TableCell
									colSpan={7}
									className="py-10 text-center text-sm text-muted-foreground"
								>
									Sem contactos pendentes.
								</TableCell>
							</TableRow>
						) : (
							rows.map(
								({
									key,
									entry,
									clientName,
									clientPhone,
									repairNumber,
									storeRepairId,
									message,
								}) => (
									<TableRow key={key} className="align-top">
										<TableCell>{repairNumber}</TableCell>
										<TableCell>{storeRepairId}</TableCell>
										<TableCell>
											{entry.client ? (
												<button
													type="button"
													className="text-left text-primary underline-offset-2 hover:underline"
													onClick={() => openClientDialog(entry.client ?? {})}
												>
													{clientName}
												</button>
											) : (
												<span className="text-muted-foreground">-</span>
											)}
										</TableCell>
										<TableCell>{clientPhone}</TableCell>
										<TableCell>{getEquipmentLabel(entry.repair)}</TableCell>
										<TableCell>
											<span className="text-sm text-foreground">{message}</span>
										</TableCell>

										<TableCell className="text-right">
											<div className="flex justify-end gap-2">
												<Button
													size="sm"
													variant="secondary"
													onClick={() => handleShowRepair(entry)}
													disabled={!entry.repair}
												>
													Ver
												</Button>
												<Popover>
													<PopoverTrigger asChild>
														<Button size="sm" className="gap-2">
															Ações
															<ChevronDown className="h-4 w-4" />
														</Button>
													</PopoverTrigger>
													<PopoverContent className="w-48 p-2">
														<div className="flex flex-col gap-1">
															{!entry.missedCall ? (
																<Button
																	variant="ghost"
																	size="sm"
																	className="justify-start"
																	onClick={() => handleMarkAsNotAnswered(entry)}
																	disabled={isLoading}
																>
																	Não atendeu
																</Button>
															) : null}
															{!entry.waitingAnswer ? (
																<Button
																	variant="ghost"
																	size="sm"
																	className="justify-start"
																	onClick={() => handleMarkAsWaiting(entry)}
																	disabled={isLoading}
																>
																	Aguardar resposta
																</Button>
															) : null}
															{!entry.isQuotation ? (
																<Button
																	variant="ghost"
																	size="sm"
																	className="justify-start"
																	onClick={() =>
																		handleMarkProcessedClick(entry)
																	}
																	disabled={isLoading}
																>
																	Resposta
																</Button>
															) : null}
															{entry.isQuotation &&
															!isToSetRepairValue(entry) ? (
																<Button
																	variant="ghost"
																	size="sm"
																	className="justify-start"
																	onClick={() => openApproveDialog(entry)}
																	disabled={isLoading}
																>
																	Aprovar
																</Button>
															) : null}
															{entry.isQuotation &&
															!isToSetRepairValue(entry) ? (
																<Button
																	variant="ghost"
																	size="sm"
																	className="justify-start text-destructive"
																	onClick={() => openRejectDialog(entry)}
																	disabled={isLoading}
																>
																	Recusar
																</Button>
															) : null}
															{isToSetRepairValue(entry) ? (
																<Button
																	variant="ghost"
																	size="sm"
																	className="justify-start"
																	onClick={() => openSetValueDialog(entry)}
																	disabled={isLoading}
																>
																	Definir valor
																</Button>
															) : null}
														</div>
													</PopoverContent>
												</Popover>
											</div>
										</TableCell>
									</TableRow>
								),
							)
						)}
					</TableBody>
				</Table>
			</CardContent>

			{isLoading ? (
				<div className="absolute inset-0 flex items-center justify-center rounded-lg bg-background/70 backdrop-blur-sm">
					<div className="flex items-center gap-2 text-sm text-muted-foreground">
						<div className="h-2 w-2 rounded-full bg-primary" />A atualizar...
					</div>
				</div>
			) : null}

			<Dialog
				open={dialogState.type === "client"}
				onOpenChange={(open) => {
					if (!open) {
						closeDialog();
					}
				}}
			>
				<DialogContent className="sm:max-w-lg">
					<DialogHeader>
						<DialogTitle>Informação do cliente</DialogTitle>
						<DialogDescription>
							Contactos e dados fiscais do cliente selecionado.
						</DialogDescription>
					</DialogHeader>
					{dialogState.type === "client" ? (
						<div className="flex flex-col gap-4 sm:flex-row">
							<div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted text-lg font-semibold text-foreground">
								{buildInitials(dialogState.client.name)}
							</div>
							<div className="space-y-2">
								<div>
									<p className="text-sm text-muted-foreground">Nome</p>
									<p className="text-base font-semibold">
										{dialogState.client.name || "-"}
									</p>
								</div>
								<div className="grid gap-3 sm:grid-cols-2">
									<div>
										<p className="text-sm text-muted-foreground">NIF</p>
										<p>{dialogState.client.taxNumber || "-"}</p>
									</div>
									<div>
										<p className="text-sm text-muted-foreground">Telefone</p>
										<p className="flex items-center gap-2">
											<Phone className="h-4 w-4 text-muted-foreground" />
											{dialogState.client.phone || "-"}
										</p>
									</div>
								</div>
								<div>
									<p className="text-sm text-muted-foreground">Email</p>
									<p>{dialogState.client.email || "-"}</p>
								</div>
							</div>
						</div>
					) : null}
					<DialogFooter>
						<Button variant="outline" type="button" onClick={closeDialog}>
							Fechar
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog
				open={dialogState.type === "approve"}
				onOpenChange={(open) => {
					if (!open) {
						closeDialog();
					}
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Aprovar orçamento</DialogTitle>
						<DialogDescription>
							Confirme o valor final antes de aprovar.
						</DialogDescription>
					</DialogHeader>
					<Form {...approveForm}>
						<form
							className="space-y-4"
							onSubmit={approveForm.handleSubmit(handleApproveQuotation)}
						>
							<FormField
								control={approveForm.control}
								name="value"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Valor</FormLabel>
										<FormControl>
											<Input
												type="number"
												step="0.01"
												disabled={isLoading}
												value={field.value}
												onChange={(event) =>
													field.onChange(parseNumberInput(event.target.value))
												}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<DialogFooter>
								<Button
									variant="outline"
									type="button"
									onClick={closeDialog}
									disabled={isLoading}
								>
									Cancelar
								</Button>
								<Button type="submit" disabled={isLoading}>
									{isLoading ? "A aprovar..." : "Aprovar"}
								</Button>
							</DialogFooter>
						</form>
					</Form>
				</DialogContent>
			</Dialog>

			<Dialog
				open={dialogState.type === "response"}
				onOpenChange={(open) => {
					if (!open) {
						closeDialog();
					}
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Resposta ao cliente</DialogTitle>
						<DialogDescription>
							Registe a resposta dada ao cliente.
						</DialogDescription>
					</DialogHeader>
					<Form {...responseForm}>
						<form
							className="space-y-4"
							onSubmit={responseForm.handleSubmit(handleResponseSubmit)}
						>
							<FormField
								control={responseForm.control}
								name="message"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Mensagem</FormLabel>
										<FormControl>
											<Input
												{...field}
												placeholder="Resposta dada ao cliente"
												disabled={isLoading}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<DialogFooter>
								<Button
									variant="outline"
									type="button"
									onClick={closeDialog}
									disabled={isLoading}
								>
									Cancelar
								</Button>
								<Button type="submit" disabled={isLoading}>
									{isLoading ? "A processar..." : "Processar"}
								</Button>
							</DialogFooter>
						</form>
					</Form>
				</DialogContent>
			</Dialog>

			<Dialog
				open={dialogState.type === "setValue"}
				onOpenChange={(open) => {
					if (!open) {
						closeDialog();
					}
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Definir valor</DialogTitle>
						<DialogDescription>
							Atualize o valor da reparação para aprovação.
						</DialogDescription>
					</DialogHeader>
					<Form {...setValueForm}>
						<form
							className="space-y-4"
							onSubmit={setValueForm.handleSubmit(handleRepairValue)}
						>
							<FormField
								control={setValueForm.control}
								name="value"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Valor</FormLabel>
										<FormControl>
											<Input
												type="number"
												step="0.01"
												disabled={isLoading}
												value={field.value}
												onChange={(event) =>
													field.onChange(parseNumberInput(event.target.value))
												}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<DialogFooter>
								<Button
									variant="outline"
									type="button"
									onClick={closeDialog}
									disabled={isLoading}
								>
									Cancelar
								</Button>
								<Button type="submit" disabled={isLoading}>
									{isLoading ? "A guardar..." : "Guardar"}
								</Button>
							</DialogFooter>
						</form>
					</Form>
				</DialogContent>
			</Dialog>

			<AlertDialog
				open={dialogState.type === "reject"}
				onOpenChange={(open) => {
					if (!open) {
						closeDialog();
					}
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Recusar orçamento</AlertDialogTitle>
						<AlertDialogDescription>
							Pretende recusar o orçamento apresentado?
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isLoading}>Cancelar</AlertDialogCancel>
						<AlertDialogAction
							className="bg-destructive text-white hover:bg-destructive/90"
							onClick={handleRejectQuotation}
							disabled={isLoading}
							aria-busy={isLoading ? "true" : undefined}
						>
							{isLoading ? "A recusar..." : "Recusar"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</Card>
	);
};
